import React, { useEffect, useState } from 'react'
import { Button } from '@mui/material'
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { CCol, CForm, CFormInput, CFormLabel, CInputGroup, CInputGroupText, CRow } from '@coreui/react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Icon } from '@iconify/react'
import { useAuth } from '../contexts/AuthContext';
import { useCombo } from '../contexts/CombosContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import CInput from './CInput';
import AddChoiceInCombo from './AddChoiceInCombo';
import CategorySelection from './CategorySelection';

const AddCombo = ({ comboDetails, editMode, editComboId, setEditComboId, setEditMode, comboVisible, setComboVisible, setComboDetails, fetchCombos, canCreate, canUpdate, canDelete }) => {
    const { createCombo, getSingleCombo, updateCombo, deleteCombo } = useCombo()
    const { restaurantId } = useAuth()
    const { currency } = useCompanySetting()

    const [validated, setValidated] = useState(false);
    const [choiceVisible, setChoiceVisible] = useState(false)
    const [editChoiceId, setEditChoiceId] = useState(null)
    const [categoryValidated, setCategoryValidated] = useState(false)

    useEffect(() => {
        fetchSingleCombo();
    }, [editMode, editComboId]);

    const fetchSingleCombo = async () => {
        if (editComboId) {
            const combo = await getSingleCombo(editComboId);
            if(combo){
                setComboDetails(combo);
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((editMode && !canUpdate) || (!editMode && !canCreate)) return;

        const form = e.currentTarget;
        setValidated(true);
        comboDetails.category_id ? setCategoryValidated(false) : setCategoryValidated(true)

        if (form.checkValidity() === false) {
            e.stopPropagation();

            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            try {
                if (comboVisible && restaurantId && comboDetails.category_id) {
                    const selectedCategory = comboDetails.category_id.value

                    const data = await createCombo({ ...comboDetails, restaurant_id: restaurantId, category_id: selectedCategory })
                    if (!data.error) {
                        fetchCombos(data.data)
                        handleClose();
                    }
                } else if (editMode && editComboId && restaurantId && comboDetails.category_id) {
                    const selectedCategory = comboDetails.category_id.value

                    const data = await updateCombo(editComboId, { ...comboDetails, restaurant_id: restaurantId, category_id: selectedCategory });
                    if (!data.error) {
                        handleClose();
                        fetchCombos()
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    const handleClose = () => {
        setComboVisible(false);
        setEditMode(false);
        setEditComboId(null);
        setComboDetails({
            name: '',
            plu: '',
            category_id: '',
            price: '',
            choices: [],
            image: '',
            visible: 0,
            finalTotalPrice: 0,
            restaurant_id: restaurantId
        });
    };

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this combo?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                await deleteCombo(editComboId);
                handleClose();
                fetchCombos()
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    }

    const handleChoiceDelete = (id) => {
        const choices = comboDetails.choices.filter((c) => c.id !== id)
        setComboDetails({ ...comboDetails, choices: choices })
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setComboDetails({ ...comboDetails, image: reader.result })
            };
            reader.readAsDataURL(file);
        }
    };

    useEffect(() => {
        calculateFinalTotalPrice();
    }, 
    [comboDetails.price, comboDetails.choices]);

    const calculateFinalTotalPrice = () => {
        const choicesTotalPrice = comboDetails.choices.reduce((acc, choice) => acc + choice.totalPrice, 0);
        const finalTotal = parseFloat(comboDetails.price) + choicesTotalPrice;
        setComboDetails({ ...comboDetails, finalTotalPrice: finalTotal });
    };

    return (
        <>
            {
                (choiceVisible || editChoiceId) &&
                <AddChoiceInCombo
                    choiceVisible={choiceVisible}
                    editChoiceId={editChoiceId}
                    comboDetails={comboDetails}
                    setComboDetails={setComboDetails}
                    setChoiceVisible={setChoiceVisible}
                    setEditChoiceId={setEditChoiceId}
                />

            }
            <ConfirmDialog />
            <Dialog draggable={false} header={`${comboVisible ? 'Create Combo' : 'Edit Combo'}`} visible={comboVisible || (editMode && editComboId !== null)} style={{ width: "57vw" }} onHide={handleClose}>
                <CForm onSubmit={handleSubmit} noValidate validated={validated}>
                    <div className='fields-container'>
                        <CRow>
                            <CCol>
                                <CRow className="mb-4 mt-3">
                                    <CInput
                                        label="Name"
                                        type="text"
                                        className='d-flex'
                                        labelClassName='w-50'
                                        value={comboDetails.name}
                                        onChange={(value) => setComboDetails({ ...comboDetails, name: value })}
                                        required={true}
                                        errorMessage='Name is required.'
                                    />
                                </CRow>
                                <CRow className="mb-4">
                                    <CInput
                                        label="PLU"
                                        type="text"
                                        className='d-flex'
                                        labelClassName='w-50'
                                        value={comboDetails.plu}
                                        onChange={(value) => setComboDetails({ ...comboDetails, plu: value })}
                                        required={true}
                                        errorMessage='PLU is required.'
                                    />
                                </CRow>
                                <CRow className='mb-4'>
                                    <CCol lg={4}>
                                        <CFormLabel className="form-label w-50">Price <span className="text-danger">*</span></CFormLabel>
                                    </CCol>
                                    <CCol lg={8} className='ps-0'>
                                        <CInputGroup>
                                            <CInputGroupText id="basic-addon1">
                                                {currency}
                                            </CInputGroupText>
                                            <CFormInput
                                                type="number"
                                                step="any"
                                                min={0}
                                                value={comboDetails.price}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    if (newValue >= 0) {
                                                        setComboDetails({ ...comboDetails, price: newValue })
                                                    }
                                                }}
                                                required
                                                feedbackInvalid='Price is required.'
                                            />
                                        </CInputGroup>
                                    </CCol>
                                </CRow>
                                <CRow className="mb-4">
                                    <CategorySelection
                                        className='d-flex'
                                        labelClassName='w-50'
                                        value={comboDetails.category_id}
                                        onChange={(value) => setComboDetails({ ...comboDetails, category_id: value })}
                                        multiSelect={false}
                                        required={true}
                                        error={categoryValidated}
                                    />
                                </CRow>
                                <CRow className='mb-4'>
                                    <CCol lg={4}>
                                        <CFormLabel className="form-label">Visible</CFormLabel>
                                    </CCol>
                                    <CCol lg={8} className="custom-checkbox px-1">
                                        <Checkbox
                                            checked={comboDetails.visible === 1}
                                            onChange={(event) => setComboDetails({ ...comboDetails, visible: event.target.checked ? 1 : 0 })}
                                        />
                                    </CCol>
                                </CRow>
                                <CRow className="mb-3">
                                    <CCol lg={4}>
                                        <CFormLabel className="form-label">Image</CFormLabel>
                                    </CCol>
                                    <CCol lg={8} style={{ position: 'relative' }} className='px-1'>
                                        <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="fileInput" />
                                        <label htmlFor="fileInput">
                                            <Card
                                                className="create-card"
                                                header={comboDetails.image ?
                                                    <LazyLoadImage
                                                        src={comboDetails.image || 'defaultImageURL'}
                                                        alt="Product"
                                                        className='create-img'
                                                    />
                                                    :
                                                    <Icon
                                                        icon="mdi:food-outline"
                                                        style={{ color: "black" }}
                                                        height={85}
                                                        width={100}
                                                    />}
                                            />
                                        </label>
                                        {
                                            comboDetails.image &&
                                            <Icon
                                                icon="mdi:close"
                                                className='close-btn'
                                                height={15}
                                                width={15}
                                                onClick={() => setComboDetails({ ...comboDetails, image: '' })}
                                            />
                                        }
                                    </CCol>
                                </CRow>
                            </CCol>
                            <CCol>
                                <CRow className='mt-3'>
                                    <CCol lg={4}>
                                        <CFormLabel className="form-label custom-word-break">Choices <span className="text-danger">*</span></CFormLabel>
                                    </CCol>
                                    <CCol lg={8} className='px-0'>
                                        <CFormLabel className="form-label link" onClick={() => setChoiceVisible(true)}>+ Add Choices</CFormLabel>
                                    </CCol>
                                </CRow>
                                <CRow>
                                    {comboDetails.choices.length !== 0 ? (
                                        comboDetails.choices.map((choice) => {
                                            return (
                                                <div key={choice.id}>
                                                    <Card
                                                        header={
                                                            <div className='d-flex w-100'>
                                                                <div className='ingridient-left-container' title={choice.name ? choice.name : 'Unknown Choice'}>
                                                                    <div className='new_ingridient_name'>
                                                                        {choice.name ? choice.name : 'Unknown Choice'}
                                                                    </div>
                                                                </div>
                                                                <div className='ingridient-quntity' title={`${currency}${choice.totalPrice}`}>
                                                                    {currency}{choice.totalPrice.toFixed(2)}
                                                                </div>
                                                                <div className='ingridient-action'>
                                                                    <Icon icon="ic:outline-edit" style={{ color: 'black' }} className='me-1' onClick={() => {
                                                                        setChoiceVisible(false)
                                                                        setEditChoiceId({ value: choice.id, label: choice.name })
                                                                    }} />
                                                                    <Icon icon="material-symbols:delete-outline" style={{ color: 'black' }} onClick={() => handleChoiceDelete(choice.id)} />
                                                                </div>
                                                            </div>}
                                                        className="ingredient-list-card"
                                                    />
                                                </div>
                                            );
                                        })
                                    ) : (
                                        ''
                                    )}
                                </CRow>
                            </CCol>
                        </CRow>
                    </div>
                    <div className={`button-container d-flex ${(editMode && editComboId && canDelete) ? 'justify-content-between' : 'justify-content-end'}`}>
                        {
                            (editMode && editComboId && canDelete) && (
                                <div>
                                    <Button color='error' variant="contained" onClick={handleDelete} >
                                        Delete
                                    </Button>
                                </div>
                            )
                        }
                        <div className='d-flex'>
                            <Button onClick={handleClose} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            {((!editMode && canCreate) || (editMode && canUpdate)) && (
                                <Button fullWidth variant="contained" color="primary" type="submit" className="ms-3">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </div>
                </CForm>
            </Dialog>
        </>
    )
}

export default AddCombo
